import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { boardApi, devRequestApi, epicLinkApi } from '@/features/project-board/api';
import { useBoard } from '@/features/project-board/BoardProvider';
import { DevRequestDraft } from '@/features/project-board/dev-request-draft';
import { DevRequestLink } from '@/features/project-board/dev-request-link';
import { EpicLinkSteps } from '@/features/project-board/epic-link-steps';
import { RegisterDevRequestDialog } from '@/features/project-board/components/RegisterDevRequestDialog';
import { DevRequestPickerDialog } from '@/features/project-board/components/DevRequestPickerDialog';
import type { DevRequest, DevRequestSpecLink, Epic, EpicDevRequestLinkResult } from '@/features/project-board/types';

const BUTTON = 'rounded border border-line px-2 py-1 text-xs text-accent disabled:opacity-50';

export function EpicDevRequestPanel({ epic }: { epic: Epic }): React.JSX.Element {
  const { reload, index } = useBoard();
  const indexedEpic = index?.epics.find((item) => item.source === epic.source && item.slug === epic.slug);
  const [id, setId] = useState(epic.devRequestId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<EpicDevRequestLinkResult | null>(null);
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(null);
  const [retryAction, setRetryAction] = useState<(() => Promise<EpicDevRequestLinkResult>) | null>(null);
  const [inbox, setInbox] = useState<{ requests: DevRequest[]; specs: DevRequestSpecLink[] } | null>(null);
  const [picker, setPicker] = useState(false);
  const [confirm, setConfirm] = useState<{ target: number | null } | null>(null);
  // 재조회 값이 이전 번호와 같아도 낙관적 연결값을 되돌린다. busy 렌더에는 색인 스냅샷이 유지된다.
  useEffect(() => { setId(indexedEpic ? indexedEpic.devRequestId : epic.devRequestId); }, [index, indexedEpic, epic.devRequestId]);
  useEffect(
    () => {
      if (result === null || EpicLinkSteps.needsRecovery(result)) return;
      const timer = window.setTimeout(() => setResult(null), 2000);
      return () => window.clearTimeout(timer);
    },
    [result],
  );
  async function save(call: () => Promise<EpicDevRequestLinkResult>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError('');
    setResult(null);
    setRetryAction(() => call);
    try {
      const value = await call();
      setResult(value);
      setId((current) => EpicLinkSteps.linkedId(value, current));
      if (value.outcome === 'completed') {
        if (!EpicLinkSteps.needsRecovery(value)) setRetryAction(null);
        reload();
        // 요청 목록은 BoardProvider와 원천이 다르다. 별도로 다시 읽어 갱신 실패도 화면에 남긴다.
        setInbox(await devRequestApi.list());
      }
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setBusy(false); }
  }
  async function openRegister(): Promise<void> {
    setBusy(true);
    setError('');
    let text = '';
    try {
      text = (await boardApi.document(epic.source, `${epic.path}/PRD.md`)).text;
    }
    catch { /* 사본이 없어도 직접 입력할 수 있게 빈 초안으로 연다. */ }
    setDraft(DevRequestDraft.fromPrd(epic, text));
    setBusy(false);
  }
  function register(value: { title: string; body: string }): void {
    setDraft(null);
    void save(() => epicLinkApi.register(epic.source, epic.slug, value));
  }
  function pick(target: number): void {
    setPicker(false);
    if (id !== null) {
      setConfirm({ target });
      return;
    }
    void save(() => epicLinkApi.link(epic.source, epic.slug, target, id));
  }
  function confirmed(): void {
    if (confirm === null) return;
    const target = confirm.target;
    setConfirm(null);
    if (target === null && id !== null) {
      void save(() => epicLinkApi.unlink(epic.source, epic.slug, id));
    }
    else if (target !== null) void save(() => epicLinkApi.link(epic.source, epic.slug, target, id));
  }
  return (
    <div className="space-y-2">
      <div>{id === null ?
        '없음' :
        <Link className="text-accent hover:underline" to={DevRequestLink.href(id)}>개발요청 #{id}</Link>}</div>
      <div className="flex flex-wrap gap-2">
        {id === null && <button
          type="button"
          disabled={busy}
          className={BUTTON}
          onClick={() => void openRegister()}
        >요청서 만들기</button>}
        <button
          type="button"
          disabled={busy}
          className={BUTTON}
          onClick={() => setPicker(true)}
        >{id === null ? '기존 요청서에 연결' : '다른 요청서로 바꾸기'}</button>
        {id !== null && <button
          type="button"
          disabled={busy}
          className={BUTTON}
          onClick={() => setConfirm({ target: null })}
        >연결 끊기</button>}
        {busy && <span role="status">저장 중…</span>}
      </div>
      {confirm && <div className="text-xs">{confirm.target === null ?
        `#${id} 연결을 끊습니다 — PRD의 두 줄이 없음으로 바뀌고 요청서는 남습니다` :
        `#${id} → #${confirm.target} 으로 바꿉니다`}
        <button
          className={BUTTON}
          disabled={busy}
          onClick={confirmed}
        >{confirm.target === null ? '끊기' : '저장'}</button>
        <button
          className={BUTTON}
          disabled={busy}
          onClick={() => setConfirm(null)}
        >취소</button></div>}
      {result && <div role="status" className="text-xs">
        <ul>{EpicLinkSteps.labelsOf(result).map((label, index) => <li key={index}>{label}</li>)}</ul>{result.outcome === 'incomplete' && <p>요청서 #{result.devRequestId} 은 만들어졌습니다. 다시 누르면 남은 단계를 이어서 합니다</p>}</div>}
      {retryAction && ((result !== null && EpicLinkSteps.needsRecovery(result)) || error) && <button
        className={BUTTON}
        disabled={busy}
        onClick={() => void save(retryAction)}
      >다시 시도</button>}
      {error && <div role="alert" className="text-xs text-rose-700">{error}
        <button
          className={BUTTON}
          disabled={busy}
          onClick={() => {
            reload();
            setError('');
            setResult(null);
            setRetryAction(null);
            setConfirm(null);
          }}
        >다시 열기</button></div>}
      {draft && <RegisterDevRequestDialog
        draft={draft}
        onClose={() => setDraft(null)}
        onSave={register}
      />}
      {picker && <DevRequestPickerDialog
        initial={inbox}
        onClose={() => setPicker(false)}
        onPick={pick}
      />}
    </div>
  );
}
